import { BACKEND_URL } from '../config/backendUrl';
/* CredentialRenewalPage — public, no auth.
 *
 * Candidate lands here from the expiry-reminder email button. Renders a
 * lightweight upload form (file + new expiry) tied to a 60-day token
 * that encodes the original document id. Submit goes to
 * /api/public/credential-renewal/:token/submit, which spawns a new
 * CandidateCertification on Bullhorn and attaches the file.
 *
 * Intentionally NOT inside the dashboard shell — we want the page to
 * look like a one-screen task, not a portal login. Brand color +
 * company logo come back from the GET context endpoint.
 */
import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ShieldCheck, Upload, Calendar, AlertCircle, RefreshCw } from 'lucide-react';

const API = BACKEND_URL;

export default function CredentialRenewalPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [newExpiry, setNewExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/credential-renewal/${token}`);
        if (!alive) return;
        setCtx(res.data);
        // Pre-fill the new expiry as a sensible default: 12 months from
        // today — covers the vast majority of UK construction credentials
        // (CSCS, GAS Safe, ECS). Recruiter can override on the form.
        const def = new Date();
        def.setFullYear(def.getFullYear() + 1);
        setNewExpiry(def.toISOString().slice(0, 10));
      } catch (err) {
        if (!alive) return;
        const status = err.response?.status;
        if (status === 410) {
          setError('This renewal link has expired. Please contact your recruiter for a fresh one.');
        } else if (status === 404) {
          setError("We couldn't find a credential linked to this link.");
        } else {
          setError('Something went wrong loading this page. Please try again later.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const accent = ctx?.primary_color || '#2D5A27';

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > (ctx?.max_file_size_mb || 10) * 1024 * 1024) {
      toast.error(`File too large. Max ${ctx?.max_file_size_mb || 10} MB.`);
      return;
    }
    const ext = f.name.split('.').pop()?.toLowerCase();
    const allowed = ctx?.allowed_file_types || ['pdf', 'jpg', 'jpeg', 'png'];
    if (!allowed.includes(ext)) {
      toast.error(`File type not allowed. Use ${allowed.join(', ').toUpperCase()}.`);
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please attach your renewed certificate first.');
      return;
    }
    if (!newExpiry) {
      toast.error('Please pick the new expiry date.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('new_expiry', newExpiry);
      const res = await axios.post(
        `${API}/api/public/credential-renewal/${token}/submit`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to submit renewal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="renewal-error-card">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="renewal-success-card">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Renewal received</h1>
          <p className="text-sm text-slate-600 mb-1">
            Thanks {ctx.candidate_name || 'there'} — your renewed{' '}
            <strong>{ctx.credential_name}</strong> is on file with {ctx.company_name || 'us'}.
          </p>
          <p className="text-sm text-slate-500">
            New expiry: <strong>{success.new_expiry_iso}</strong>
          </p>
          <p className="text-xs text-slate-400 mt-6">You can close this tab.</p>
        </div>
      </div>
    );
  }

  if (ctx.already_renewed_at) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Already renewed</h1>
          <p className="text-sm text-slate-600">
            This credential has already been renewed through this link. If you need to update it again, please contact your recruiter at {ctx.company_name || 'your agency'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="credential-renewal-page">
      <div className="max-w-md mx-auto">
        {/* Branding */}
        <div className="text-center mb-6">
          {ctx.company_logo_url ? (
            <img
              src={ctx.company_logo_url}
              alt={ctx.company_name || 'Agency logo'}
              className="h-10 mx-auto mb-3 object-contain"
            />
          ) : (
            <div className="text-sm font-semibold tracking-tight" style={{ color: accent }}>
              {ctx.company_name || 'HyreDocs'}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-5" style={{ background: accent, color: '#fff' }}>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider opacity-90">Credential renewal</span>
            </div>
            <h1 className="text-lg font-semibold leading-tight">{ctx.credential_name}</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <p className="text-sm text-slate-600">
              Hi {ctx.candidate_name || 'there'}, upload your renewed certificate below and we'll update your record with {ctx.company_name || 'your agency'} on Bullhorn automatically.
            </p>

            {ctx.current_expiry_iso && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                Current expiry on file: <strong>{ctx.current_expiry_iso}</strong>
              </div>
            )}

            {/* File picker */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Upload className="w-4 h-4 text-slate-500" /> Renewed certificate
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 hover:border-slate-300 rounded-xl px-4 py-6 text-center cursor-pointer transition-colors"
                data-testid="renewal-file-dropzone"
              >
                {file ? (
                  <>
                    <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{(file.size / 1024).toFixed(0)} KB · click to change</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600">
                      <span className="font-medium" style={{ color: accent }}>Choose a file</span> or drag &amp; drop
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      PDF, JPG or PNG · up to {ctx.max_file_size_mb} MB
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="renewal-file-input"
                />
              </div>
            </div>

            {/* New expiry */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5" htmlFor="new-expiry">
                <Calendar className="w-4 h-4 text-slate-500" /> New expiry date
              </label>
              <input
                id="new-expiry"
                type="date"
                value={newExpiry}
                onChange={(e) => setNewExpiry(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': accent }}
                required
                data-testid="renewal-expiry-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !file}
              className="w-full h-11 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: accent }}
              data-testid="renewal-submit-button"
            >
              {submitting ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Filing renewal…</>
              ) : (
                <>Submit renewal</>
              )}
            </button>

            <p className="text-[11px] text-center text-slate-400 leading-relaxed">
              Powered by HyreDocs · Your file is uploaded directly to {ctx.company_name || 'your agency'}'s Bullhorn record.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
